import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from "@angular/core";
import { FormControl, FormGroup } from "@angular/forms";
import {
  TikTokCreatorInfo,
  TikTokDestinationPlatformData,
} from "app/js/src/domain/destinations";
import {
  PublishBlocker,
  PublishStateService,
} from "app/js/src/infrastructure/clipping-studio/publish-state.service";
import { Subscription } from "rxjs";

const PUBLIC_PRIVACY = "PUBLIC_TO_EVERYONE";
const FRIENDS_PRIVACY = "MUTUAL_FOLLOW_FRIENDS";
const DISCLOSURE_REQUIRED_MESSAGE =
  "You need to indicate if your content promotes yourself, a third party, or both.";
const BRANDED_PRIVACY_MESSAGE =
  "Branded content visibility cannot be set to private.";

type TikTokPublishForm = {
  privacyLevel: FormControl<string | null>;
  allowComment: FormControl<boolean>;
  allowDuet: FormControl<boolean>;
  allowStitch: FormControl<boolean>;
  contentDisclosureEnabled: FormControl<boolean>;
  isBrandContent: FormControl<boolean>;
  isBrandOrganic: FormControl<boolean>;
  isAigc: FormControl<boolean>;
};

@Component({
  selector: "evrideo-clipping-studio-tiktok-publish-settings",
  templateUrl: "./clipping-studio-tiktok-publish-settings.component.html",
  styleUrls: ["./clipping-studio-tiktok-publish-settings.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClippingStudioTikTokPublishSettingsComponent implements OnChanges, OnDestroy {
  @Input({ required: true }) destinationUid!: string;
  @Input({ required: true }) destinationName!: string;
  @Input() creatorInfo: TikTokCreatorInfo | null = null;
  @Input() platformData: TikTokDestinationPlatformData | null = null;
  @Input() readonly = false;

  @Output() platformDataChange = new EventEmitter<TikTokDestinationPlatformData>();

  readonly form = new FormGroup<TikTokPublishForm>({
    privacyLevel: new FormControl<string | null>(null),
    allowComment: new FormControl<boolean>(false, { nonNullable: true }),
    allowDuet: new FormControl<boolean>(false, { nonNullable: true }),
    allowStitch: new FormControl<boolean>(false, { nonNullable: true }),
    contentDisclosureEnabled: new FormControl<boolean>(false, { nonNullable: true }),
    isBrandContent: new FormControl<boolean>(false, { nonNullable: true }),
    isBrandOrganic: new FormControl<boolean>(false, { nonNullable: true }),
    isAigc: new FormControl<boolean>(false, { nonNullable: true }),
  });

  readonly disclosureRequiredMessage = DISCLOSURE_REQUIRED_MESSAGE;
  readonly brandedPrivacyMessage = BRANDED_PRIVACY_MESSAGE;

  private readonly subscription = new Subscription();
  private syncing = false;

  constructor(
    private readonly publishState: PublishStateService,
    private readonly changeDetectorRef: ChangeDetectorRef,
  ) {
    this.subscription.add(
      this.form.valueChanges.subscribe(() => this.onFormChanged()),
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes["creatorInfo"] ||
      changes["platformData"] ||
      changes["destinationUid"] ||
      changes["destinationName"] ||
      changes["readonly"]
    ) {
      this.syncFromInputs();
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  get privacyOptions(): readonly string[] {
    return this.creatorInfo?.privacyLevelOptions ?? [];
  }

  get shouldShowCommercialOptions(): boolean {
    return this.form.controls.contentDisclosureEnabled.value;
  }

  get shouldShowPromotionalContentMessage(): boolean {
    return this.form.controls.isBrandOrganic.value && !this.form.controls.isBrandContent.value;
  }

  get shouldShowPaidPartnershipMessage(): boolean {
    return this.form.controls.isBrandContent.value;
  }

  get privacyBlockerMessage(): string | null {
    const privacy = this.form.controls.privacyLevel.value;
    if (!privacy) {
      return "Select TikTok privacy status.";
    }
    return this.privacyFieldError;
  }

  get privacyFieldError(): string | null {
    const privacy = this.form.controls.privacyLevel.value;
    if (!privacy) {
      return null;
    }
    if (!this.privacyOptions.includes(privacy)) {
      return "The selected TikTok privacy status is no longer available for this account.";
    }
    if (this.form.controls.isBrandContent.value && !this.isBrandedContentPrivacyAllowed(privacy)) {
      return BRANDED_PRIVACY_MESSAGE;
    }
    return null;
  }

  get commercialDisclosureError(): string | null {
    if (!this.form.controls.contentDisclosureEnabled.value) {
      return null;
    }
    if (!this.form.controls.isBrandContent.value && !this.form.controls.isBrandOrganic.value) {
      return DISCLOSURE_REQUIRED_MESSAGE;
    }
    return null;
  }

  resetSettings(): void {
    this.syncing = true;
    this.form.reset(
      {
        privacyLevel: null,
        allowComment: false,
        allowDuet: false,
        allowStitch: false,
        contentDisclosureEnabled: false,
        isBrandContent: false,
        isBrandOrganic: false,
        isAigc: false,
      },
      { emitEvent: false },
    );
    this.applyCreatorInfoLocks();
    this.syncing = false;
    this.onFormChanged();
    this.changeDetectorRef.markForCheck();
  }

  onPrivacyChange(value: string): void {
    if (this.readonly || this.isPrivacyOptionDisabled(value)) {
      this.changeDetectorRef.markForCheck();
      return;
    }
    this.form.controls.privacyLevel.setValue(value);
  }

  isPrivacyOptionDisabled(option: string): boolean {
    return this.form.controls.isBrandContent.value && !this.isBrandedContentPrivacyAllowed(option);
  }

  private syncFromInputs(): void {
    this.syncing = true;
    const data = this.platformData;
    this.form.reset(
      {
        privacyLevel: data?.privacy_level ?? null,
        allowComment: !(data?.disable_comment ?? true),
        allowDuet: !(data?.disable_duet ?? true),
        allowStitch: !(data?.disable_stitch ?? true),
        contentDisclosureEnabled: data?.content_disclosure_enabled ?? false,
        isBrandContent: data?.is_brand_content ?? false,
        isBrandOrganic: data?.is_brand_organic ?? false,
        isAigc: data?.is_aigc ?? false,
      },
      { emitEvent: false },
    );

    this.applyCreatorInfoLocks();
    this.syncing = false;
    this.normalizeDisclosureState();
    this.updatePublishGateFromForm();
    this.changeDetectorRef.markForCheck();
  }

  private applyCreatorInfoLocks(): void {
    this.setInteractionControlState("allowComment", this.creatorInfo?.commentDisabled === true);
    this.setInteractionControlState("allowDuet", this.creatorInfo?.duetDisabled === true);
    this.setInteractionControlState("allowStitch", this.creatorInfo?.stitchDisabled === true);
  }

  private setInteractionControlState(
    controlName: "allowComment" | "allowDuet" | "allowStitch",
    creatorDisabled: boolean,
  ): void {
    const control = this.form.controls[controlName];
    if (creatorDisabled || this.readonly) {
      control.setValue(false, { emitEvent: false });
      control.disable({ emitEvent: false });
      return;
    }
    control.enable({ emitEvent: false });
  }

  private onFormChanged(): void {
    if (this.syncing) {
      return;
    }
    this.normalizeDisclosureState();
    const platformData = this.toPlatformData();
    this.platformDataChange.emit(platformData);
    this.updatePublishGateFromForm();
    this.changeDetectorRef.markForCheck();
  }

  private updatePublishGateFromForm(): void {
    this.publishState.setTikTokPublishContext({
      destinationUid: this.destinationUid,
      destinationName: this.destinationName,
      maxVideoPostDurationSec: this.creatorInfo?.maxVideoPostDurationSec ?? 0,
      isBrandContent: this.form.controls.isBrandContent.value,
    });
    this.publishState.setPublishBlockers(this.blockerSource, this.collectBlockers());
  }

  private normalizeDisclosureState(): void {
    if (this.form.controls.contentDisclosureEnabled.value) {
      return;
    }
    if (this.form.controls.isBrandContent.value || this.form.controls.isBrandOrganic.value) {
      this.form.patchValue(
        {
          isBrandContent: false,
          isBrandOrganic: false,
        },
        { emitEvent: false },
      );
    }
  }

  private toPlatformData(): TikTokDestinationPlatformData {
    const raw = this.form.getRawValue();
    return {
      privacy_level: raw.privacyLevel,
      disable_comment: !raw.allowComment,
      disable_duet: !raw.allowDuet,
      disable_stitch: !raw.allowStitch,
      publish_mode: "DIRECT_POST",
      content_disclosure_enabled: raw.contentDisclosureEnabled,
      is_brand_content: raw.isBrandContent,
      is_brand_organic: raw.isBrandOrganic,
      is_aigc: raw.isAigc,
      max_video_post_duration_sec: this.creatorInfo?.maxVideoPostDurationSec ?? null,
    };
  }

  private collectBlockers(): PublishBlocker[] {
    const blockers: PublishBlocker[] = [];
    if (!this.creatorInfo) {
      blockers.push({
        code: "tiktok_creator_info_missing",
        message: "TikTok account settings could not be loaded.",
      });
      return blockers;
    }
    if (this.privacyBlockerMessage) {
      blockers.push({ code: "tiktok_privacy_invalid", message: this.privacyBlockerMessage });
    }
    if (this.commercialDisclosureError) {
      blockers.push({
        code: "tiktok_commercial_disclosure_required",
        message: this.commercialDisclosureError,
      });
    }
    return blockers;
  }

  private isBrandedContentPrivacyAllowed(privacy: string | null): boolean {
    return privacy === PUBLIC_PRIVACY || privacy === FRIENDS_PRIVACY;
  }

  private get blockerSource(): string {
    return `tiktok:${this.destinationUid}`;
  }
}
